import React, { useEffect, useState, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { DrawerContext } from '../components/layout/Layout';
import { Box, Typography, IconButton } from '@mui/material';
import {
  WbSunny, Public, RocketLaunch, SatelliteAlt,
  Nightlight, Explore, FlashOn, WbCloudy, CalendarMonth,
  Newspaper, MenuBook, Notifications, KeyboardArrowDown,
  Brightness3, WbIncandescent,
} from '@mui/icons-material';
import SpacePulseLogo from '../components/common/SpacePulseLogo';

// ─── Background images ────────────────────────────────────────────────────────
const IMAGES = {
  hero:       'https://images.unsplash.com/photo-1419242902214-272b3f66ee7a?w=1920&q=80&auto=format&fit=crop',

  // NASA Solar Dynamics Observatory — real EUV 193Å corona image (gold/orange sun)
  solar:      'https://sdo.gsfc.nasa.gov/assets/img/latest/latest_1024_0193.jpg',

  solarSys:   'https://images.unsplash.com/photo-1462331940025-496dfbfc7564?w=1920&q=80&auto=format&fit=crop',

  // NASA Hubble — Carina Nebula (deep purple/violet — perfect for AI section)
  nova:       'https://imagine.gsfc.nasa.gov/hera/apod/image/0304/carina_hst_big.jpg',

  iss:        'https://images.unsplash.com/photo-1446776811953-b23d57bd21aa?w=1920&q=80&auto=format&fit=crop',
  satellite:  'https://images.unsplash.com/photo-1517976547714-720226b864c1?w=1920&q=80&auto=format&fit=crop',

  // Real full-moon photograph — Wikimedia Commons public domain (Gregory H. Revera)
  moon:       'https://upload.wikimedia.org/wikipedia/commons/thumb/e/e1/FullMoon2010.jpg/1920px-FullMoon2010.jpg',

  // Earth at night — city lights from space (Unsplash, CC0)
  nightEarth: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&q=85&auto=format&fit=crop',

  // NASA Blue Marble — real Earth photograph from Apollo 17
  earthWeather: 'https://images.unsplash.com/photo-1614730321146-b6fa6a46bcb4?w=1920&q=85&auto=format&fit=crop',
};

// ─── Inject fonts ─────────────────────────────────────────────────────────────
const fontLink = document.createElement('link');
fontLink.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap';
fontLink.rel  = 'stylesheet';
if (!document.head.querySelector('[href*="Inter"]')) document.head.appendChild(fontLink);


// ─── Ghost Button (SpaceX-style) ──────────────────────────────────────────────
function GhostButton({ children, onClick, light = false }) {
  const [hov, setHov] = useState(false);
  return (
    <Box
      component="button"
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      sx={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        px: { xs: 2.5, md: 4 }, py: { xs: 0.9, md: 1.5 },
        fontSize: { xs: 10, md: 12 },
        fontWeight: 600, letterSpacing: 2.5,
        fontFamily: 'Inter, sans-serif',
        textTransform: 'uppercase',
        cursor: 'pointer',
        border: light ? '1px solid rgba(255,255,255,0.85)' : '1px solid rgba(255,255,255,0.45)',
        color: '#fff',
        bgcolor: hov ? 'rgba(255,255,255,0.14)' : 'transparent',
        borderColor: hov ? 'rgba(255,255,255,0.9)' : (light ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.45)'),
        transition: 'background-color 0.2s, border-color 0.2s',
        outline: 'none',
        minWidth: { xs: 130, md: 160 },
        borderRadius: 0,
        '&:active': { bgcolor: 'rgba(255,255,255,0.08)' },
      }}
    >
      {children}
    </Box>
  );
}

// ─── Full-screen Section ──────────────────────────────────────────────────────
function FullSection({ imgUrl, overlay, children, minH, id }) {
  minH = minH || { xs: '100svh', md: '100vh' };
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVis(true); obs.disconnect(); }
    }, { threshold: 0.12 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <Box
      ref={ref}
      id={id}
      sx={{
        position: 'relative',
        height: minH,
        scrollSnapAlign: 'start',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        pb: { xs: 5, md: 10 },
        px: { xs: 3, sm: 5, md: 10 },
        backgroundImage: `url(${imgUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundAttachment: 'scroll',
        overflow: 'hidden',
      }}
    >
      {/* Dark overlay */}
      <Box sx={{
        position: 'absolute', inset: 0,
        background: overlay || 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.45) 50%, rgba(0,0,0,0.2) 100%)',
        zIndex: 0,
      }} />

      {/* Content */}
      <Box sx={{
        position: 'relative', zIndex: 1,
        opacity: vis ? 1 : 0,
        transform: vis ? 'translateY(0)' : 'translateY(36px)',
        transition: 'opacity 1s ease, transform 1s ease',
      }}>
        {children}
      </Box>
    </Box>
  );
}

// ─── Section Label (like SpaceX section tag) ──────────────────────────────────
function SectionTag({ children }) {
  return (
    <Typography sx={{
      display: 'inline-block',
      fontSize: { xs: 9, md: 11 },
      fontWeight: 600, letterSpacing: 3,
      textTransform: 'uppercase',
      color: 'rgba(255,255,255,0.45)',
      fontFamily: 'Inter, sans-serif',
      mb: 1.5,
      borderLeft: '2px solid rgba(255,255,255,0.3)',
      pl: 1.5,
    }}>
      {children}
    </Typography>
  );
}

// ─── Section Heading ──────────────────────────────────────────────────────────
function SectionTitle({ children }) {
  return (
    <Typography sx={{
      fontSize: { xs: '1.6rem', sm: '2.4rem', md: '4rem', lg: '5rem' },
      fontWeight: 800, color: '#fff',
      letterSpacing: { xs: 0.5, md: 1 }, lineHeight: 1.05,
      fontFamily: 'Inter, sans-serif', mb: { xs: 1, md: 2 },
    }}>
      {children}
    </Typography>
  );
}

// ─── Section Description ──────────────────────────────────────────────────────
function SectionDesc({ children }) {
  return (
    <Typography sx={{
      fontSize: { xs: 12, md: 15 },
      color: 'rgba(255,255,255,0.6)',
      maxWidth: 500, lineHeight: 1.7, mb: { xs: 2, md: 4 },
      fontFamily: 'Inter, sans-serif',
    }}>
      {children}
    </Typography>
  );
}

// ─── Quick Feature Tile ────────────────────────────────────────────────────────
function FeatureTile({ icon, label, path, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <Box
      onClick={() => onClick(path)}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      sx={{
        cursor: 'pointer',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        gap: { xs: 0.8, md: 1.5 },
        py: { xs: 2, md: 4 },
        px: { xs: 0.5, md: 2 },
        borderTop: '1px solid rgba(255,255,255,0.08)',
        transition: 'background 0.2s',
        bgcolor: hov ? 'rgba(255,255,255,0.06)' : 'transparent',
        '&:hover': { borderTopColor: 'rgba(255,255,255,0.25)' },
      }}
    >
      <Box sx={{
        color: hov ? '#fff' : 'rgba(255,255,255,0.45)',
        transition: 'color 0.2s, transform 0.2s',
        transform: hov ? 'scale(1.15)' : 'scale(1)',
        '& .MuiSvgIcon-root': { fontSize: { xs: 22, md: 28 } },
      }}>
        {icon}
      </Box>
      <Typography sx={{
        fontSize: { xs: 9, md: 11 },
        fontWeight: 600,
        letterSpacing: { xs: 0.8, md: 1.5 },
        textTransform: 'uppercase',
        fontFamily: 'Inter, sans-serif',
        color: hov ? '#fff' : 'rgba(255,255,255,0.45)',
        transition: 'color 0.2s',
        textAlign: 'center',
        lineHeight: 1.3,
      }}>
        {label}
      </Typography>
    </Box>
  );
}

// ─── Stars background canvas ──────────────────────────────────────────────────
function HeroStars() {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext('2d');
    let id;
    const stars = Array.from({ length: 250 }, () => ({
      x: Math.random(), y: Math.random(),
      r: Math.random() * 1.1 + 0.2,
      phase: Math.random() * Math.PI * 2,
      speed: Math.random() * 0.007 + 0.002,
    }));
    const draw = () => {
      const W = c.width = c.offsetWidth, H = c.height = c.offsetHeight;
      ctx.clearRect(0, 0, W, H);
      stars.forEach(s => {
        s.phase += s.speed;
        ctx.beginPath();
        ctx.arc(s.x * W, s.y * H, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${0.25 + Math.sin(s.phase) * 0.45})`;
        ctx.fill();
      });
      id = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(id);
  }, []);
  return (
    <canvas ref={ref} style={{
      position: 'absolute', inset: 0, zIndex: 0,
      width: '100%', height: '100%', pointerEvents: 'none',
    }} />
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function HomePage() {
  const navigate      = useNavigate();
  const setDrawerOpen = useContext(DrawerContext);
  const [vis, setVis] = useState(false);

  useEffect(() => { const t = setTimeout(() => setVis(true), 100); return () => clearTimeout(t); }, []);

  const handleDashboard = () => {
    if (window.innerWidth >= 900) navigate('/dashboard');
    else setDrawerOpen(true);
  };

  const quickLinks = [
    { icon: <WbSunny />,       label: 'Space\nWeather',    path: '/dashboard'         },
    { icon: <FlashOn />,       label: 'Events',             path: '/events'            },
    { icon: <Public />,        label: 'Solar\nSystem',      path: '/solar-system'      },
    { icon: <SatelliteAlt />,  label: 'ISS\nTracker',       path: '/iss-tracker'       },
    { icon: <Brightness3 />,   label: 'Moon\nDashboard',    path: '/moon'              },
    { icon: <Nightlight />,    label: 'Night\nSky',         path: '/night-sky'         },
    { icon: <Explore />,       label: 'Exoplanets',         path: '/exoplanets'        },
    { icon: <WbCloudy />,      label: 'Earth\nWeather',     path: '/earth-weather'     },
    { icon: <CalendarMonth />, label: 'AstroTrack',         path: '/space-calendar'    },
    { icon: <RocketLaunch />,  label: 'Missions',           path: '/space-missions'    },
    { icon: <Newspaper />,     label: 'Space\nNews',        path: '/space-news'        },
    { icon: <MenuBook />,      label: 'Study\nZone',        path: '/study-zone'        },
    { icon: <Notifications />, label: 'My\nAlerts',         path: '/alerts'            },
    { icon: <WbIncandescent />, label: 'Light\nPollution',   path: '/stargazing'        },
  ];

  return (
    <Box sx={{
      bgcolor: '#000',
      fontFamily: 'Inter, sans-serif',
      overflowX: 'hidden',
    }}>

      {/* ══════════════════════════════════════════════════════════════
          HERO SECTION — starfield canvas + big logo centered
      ══════════════════════════════════════════════════════════════ */}
      <Box sx={{
        position: 'relative',
        height: { xs: '100svh', md: '100vh' },
        scrollSnapAlign: 'start',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'flex-start',
        pt: { xs: '12vh', sm: '14vh', md: '16vh' },
        bgcolor: '#000',
        overflow: 'hidden',
      }}>
        <HeroStars />

        {/* subtle vignette */}
        <Box sx={{
          position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none',
          background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.7) 100%)',
        }} />

        {/* Center content */}
        <Box sx={{
          position: 'relative', zIndex: 2,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', gap: { xs: 1.5, md: 3 },
          px: { xs: 2, md: 0 },
          opacity: vis ? 1 : 0,
          transform: vis ? 'translateY(0)' : 'translateY(24px)',
          transition: 'opacity 1s ease 0.2s, transform 1s ease 0.2s',
        }}>

          {/* Logo mark */}
          <SpacePulseLogo size={{ xs: 44, md: 64 }} showText={false} />

          {/* SPACEPULSE wordmark — fluid sizing so it never overflows */}
          <Box sx={{ textAlign: 'center', width: '100%', px: { xs: 1, md: 0 } }}>
            <Box sx={{
              display: 'flex', alignItems: 'baseline',
              justifyContent: 'center', flexWrap: 'nowrap',
              gap: { xs: '0.3vw', sm: '0.5vw', md: 1 },
            }}>
              <Typography sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 900,
                fontSize: 'clamp(1.8rem, 9vw, 9rem)',
                color: '#fff',
                letterSpacing: 'clamp(2px, 1.2vw, 22px)',
                textTransform: 'uppercase',
                lineHeight: 1,
                whiteSpace: 'nowrap',
              }}>
                Space
              </Typography>
              <Typography sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 200,
                fontSize: 'clamp(1.8rem, 9vw, 9rem)',
                color: 'rgba(255,255,255,0.7)',
                letterSpacing: 'clamp(2px, 1.2vw, 22px)',
                textTransform: 'uppercase',
                lineHeight: 1,
                whiteSpace: 'nowrap',
              }}>
                Pulse
              </Typography>
            </Box>
            {/* Mobile — all 5 lines */}
            <Typography sx={{
              display: { xs: 'block', md: 'none' },
              fontSize: 11,
              fontWeight: 300,
              color: 'rgba(255,255,255,0.55)',
              fontFamily: 'Inter, sans-serif',
              lineHeight: 1.9,
              mt: 1.5,
              maxWidth: '90vw',
              textAlign: 'center',
              letterSpacing: 0.3,
            }}>
              The universe never sleeps.<br />
              Stars burn. Storms rage. Orbits never stop.<br />
              From solar flares to distant exoplanets,<br />
              the cosmos is always in motion —<br />
              <Box component="span" sx={{ color: '#fff', fontWeight: 500 }}>
                Your Universe is Live.
              </Box>
            </Typography>

            {/* Desktop — all 5 lines, centered */}
            <Typography sx={{
              display: { xs: 'none', md: 'block' },
              fontSize: 16,
              fontWeight: 300,
              color: 'rgba(255,255,255,0.55)',
              fontFamily: 'Inter, sans-serif',
              lineHeight: 2,
              mt: 2,
              textAlign: 'center',
              letterSpacing: 0.4,
            }}>
              The universe never sleeps.<br />
              Stars burn. Storms rage. Orbits never stop.<br />
              From solar flares to distant exoplanets,<br />
              the cosmos is always in motion —<br />
              <Box component="span" sx={{ color: '#fff', fontWeight: 500 }}>
                Your Universe is Live.
              </Box>
            </Typography>
          </Box>

          {/* CTA Buttons */}
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1.2, md: 2 },
            mt: { xs: 1, md: 2 },
            width: { xs: '100%', sm: 'auto' },
            alignItems: 'center',
          }}>
            <GhostButton onClick={handleDashboard} light>Explore Dashboard</GhostButton>
            <GhostButton onClick={() => navigate('/ai-chat')}>Ask NOVA AI</GhostButton>
          </Box>
        </Box>

        {/* Scroll arrow */}
        <Box sx={{
          position: 'absolute', bottom: 24, left: '50%',
          transform: 'translateX(-50%)', zIndex: 2,
          opacity: vis ? 0.5 : 0,
          transition: 'opacity 1s ease 1.4s',
          animation: 'bounce 2.4s ease-in-out infinite',
          '@keyframes bounce': {
            '0%,100%': { transform: 'translateX(-50%) translateY(0)' },
            '50%':     { transform: 'translateX(-50%) translateY(10px)' },
          },
        }}>
          <IconButton sx={{ color: '#fff', p: 0 }}>
            <KeyboardArrowDown sx={{ fontSize: 32 }} />
          </IconButton>
        </Box>
      </Box>

      {/* ══════════════════════════════════════════════════════════════
          SPACE WEATHER — orange solar glow
      ══════════════════════════════════════════════════════════════ */}
      <FullSection
        id="space-weather"
        imgUrl={IMAGES.solar}
        overlay="linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(10,4,0,0.45) 50%, rgba(0,0,0,0.15) 100%)"
      >
        {/* orange glow orb */}
        <Box sx={{
          position: 'absolute', top: '15%', right: { xs: '-10%', md: '5%' },
          width: { xs: 200, md: 480 }, height: { xs: 200, md: 480 },
          borderRadius: '50%', pointerEvents: 'none', zIndex: 0,
          background: 'radial-gradient(circle, rgba(255,120,20,0.22) 0%, rgba(255,60,0,0.1) 45%, transparent 70%)',
          filter: 'blur(30px)',
        }} />
        <SectionTag>Powered by NASA DONKI</SectionTag>
        <SectionTitle>Space Weather</SectionTitle>
        <SectionDesc>
          Monitor real-time solar flares, coronal mass ejections and geomagnetic
          storms. Live data updated every minute from NASA's space weather API.
        </SectionDesc>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <GhostButton onClick={() => navigate('/dashboard')}>Launch Dashboard</GhostButton>
          <GhostButton onClick={() => navigate('/events')}>View Events</GhostButton>
          <GhostButton onClick={() => navigate('/alerts')}>Space Weather Alerts</GhostButton>
        </Box>
      </FullSection>

      {/* ══════════════════════════════════════════════════════════════
          EARTH WEATHER — teal/blue glow
      ══════════════════════════════════════════════════════════════ */}
      <FullSection
        id="earth-weather"
        imgUrl={IMAGES.earthWeather}
        overlay="linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,10,30,0.55) 50%, rgba(0,0,0,0.15) 100%)"
      >
        {/* teal glow orb */}
        <Box sx={{
          position: 'absolute', top: '15%', right: { xs: '-8%', md: '6%' },
          width: { xs: 180, md: 440 }, height: { xs: 180, md: 440 },
          borderRadius: '50%', pointerEvents: 'none', zIndex: 0,
          background: 'radial-gradient(circle, rgba(0,200,180,0.2) 0%, rgba(0,120,150,0.1) 45%, transparent 70%)',
          filter: 'blur(30px)',
        }} />
        <SectionTag>Open-Meteo · 7-Day Forecast</SectionTag>
        <SectionTitle>Earth Weather</SectionTitle>
        <SectionDesc>
          Real-time weather for any city on Earth. Temperature, wind, humidity,
          UV index, 7-day forecast and AI-powered weather analysis — all in one place.
        </SectionDesc>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <GhostButton onClick={() => navigate('/earth-weather')}>Check Weather</GhostButton>
          <GhostButton onClick={() => navigate('/earth-weather')}>Live Weather Map</GhostButton>
        </Box>
      </FullSection>

      {/* ══════════════════════════════════════════════════════════════
          3D SOLAR SYSTEM — blue glow
      ══════════════════════════════════════════════════════════════ */}
      <FullSection
        id="solar-system"
        imgUrl={IMAGES.solarSys}
        overlay="linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,5,20,0.6) 50%, rgba(0,0,0,0.25) 100%)"
      >
        {/* blue glow orb */}
        <Box sx={{
          position: 'absolute', top: '20%', left: { xs: '-8%', md: '8%' },
          width: { xs: 180, md: 450 }, height: { xs: 180, md: 450 },
          borderRadius: '50%', pointerEvents: 'none', zIndex: 0,
          background: 'radial-gradient(circle, rgba(29,155,240,0.2) 0%, rgba(0,80,200,0.1) 45%, transparent 70%)',
          filter: 'blur(30px)',
        }} />
        <SectionTag>Kepler Orbital Mechanics</SectionTag>
        <SectionTitle>3D Solar System</SectionTitle>
        <SectionDesc>
          Explore an interactive 3D model of our solar system. Real-time planetary
          positions, orbital paths, and zoom from Mercury to Neptune.
        </SectionDesc>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <GhostButton onClick={() => navigate('/solar-system')}>Explore Now</GhostButton>
          <GhostButton onClick={() => navigate('/exoplanets')}>5000+ Exoplanets</GhostButton>
        </Box>
      </FullSection>

      {/* ══════════════════════════════════════════════════════════════
          NOVA AI — purple glow
      ══════════════════════════════════════════════════════════════ */}
      <FullSection
        id="nova-ai"
        imgUrl={IMAGES.nova}
        overlay="linear-gradient(to top, rgba(0,0,0,0.90) 0%, rgba(5,0,18,0.45) 50%, rgba(0,0,0,0.15) 100%)"
      >
        {/* purple glow orb */}
        <Box sx={{
          position: 'absolute', bottom: '20%', right: { xs: '-5%', md: '10%' },
          width: { xs: 160, md: 400 }, height: { xs: 160, md: 400 },
          borderRadius: '50%', pointerEvents: 'none', zIndex: 0,
          background: 'radial-gradient(circle, rgba(130,60,255,0.25) 0%, rgba(80,0,200,0.1) 45%, transparent 70%)',
          filter: 'blur(28px)',
        }} />

        {/* Robot AI illustration */}
        <Box sx={{
          position: 'absolute', right: { xs: '5%', md: '8%' }, bottom: { xs: '22%', md: '8%' },
          width: { xs: 140, md: 260 }, zIndex: 1, pointerEvents: 'none',
          filter: 'drop-shadow(0 0 32px rgba(130,60,255,0.55))',
          animation: 'novaFloat 4s ease-in-out infinite',
          '@keyframes novaFloat': {
            '0%, 100%': { transform: 'translateY(0px)' },
            '50%': { transform: 'translateY(-14px)' },
          },
        }}>
          <svg viewBox="0 0 200 280" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Antenna */}
            <line x1="100" y1="10" x2="100" y2="35" stroke="#a855f7" strokeWidth="3" strokeLinecap="round"/>
            <circle cx="100" cy="7" r="5" fill="#c084fc"/>
            <circle cx="100" cy="7" r="8" fill="none" stroke="#a855f7" strokeWidth="1.5" opacity="0.6"/>
            {/* Head */}
            <rect x="60" y="35" width="80" height="65" rx="16" fill="#1a0a2e" stroke="#7c3aed" strokeWidth="2"/>
            {/* Eyes */}
            <rect x="72" y="52" width="22" height="14" rx="6" fill="#a855f7"/>
            <rect x="106" y="52" width="22" height="14" rx="6" fill="#a855f7"/>
            <circle cx="83" cy="59" r="4" fill="#e9d5ff"/>
            <circle cx="117" cy="59" r="4" fill="#e9d5ff"/>
            <circle cx="85" cy="57" r="1.5" fill="white"/>
            <circle cx="119" cy="57" r="1.5" fill="white"/>
            {/* Mouth */}
            <rect x="76" y="78" width="48" height="8" rx="4" fill="#7c3aed" opacity="0.8"/>
            <rect x="80" y="80" width="8" height="4" rx="2" fill="#c084fc"/>
            <rect x="92" y="80" width="8" height="4" rx="2" fill="#c084fc"/>
            <rect x="104" y="80" width="8" height="4" rx="2" fill="#c084fc"/>
            <rect x="116" y="80" width="8" height="4" rx="2" fill="#c084fc"/>
            {/* Neck */}
            <rect x="88" y="100" width="24" height="14" rx="4" fill="#0f0520" stroke="#6d28d9" strokeWidth="1.5"/>
            {/* Body */}
            <rect x="45" y="114" width="110" height="90" rx="18" fill="#120828" stroke="#7c3aed" strokeWidth="2"/>
            {/* Chest panel */}
            <rect x="62" y="128" width="76" height="50" rx="10" fill="#0d0618" stroke="#6d28d9" strokeWidth="1.5"/>
            {/* Chest orb */}
            <circle cx="100" cy="148" r="14" fill="#1e0a3c" stroke="#a855f7" strokeWidth="1.5"/>
            <circle cx="100" cy="148" r="9" fill="#7c3aed" opacity="0.7"/>
            <circle cx="100" cy="148" r="5" fill="#c084fc"/>
            <circle cx="97" cy="145" r="2" fill="white" opacity="0.8"/>
            {/* Side lights */}
            <circle cx="68" cy="165" r="4" fill="#a855f7" opacity="0.9"/>
            <circle cx="132" cy="165" r="4" fill="#a855f7" opacity="0.9"/>
            {/* Arms */}
            <rect x="10" y="118" width="30" height="72" rx="12" fill="#110726" stroke="#6d28d9" strokeWidth="1.5"/>
            <rect x="160" y="118" width="30" height="72" rx="12" fill="#110726" stroke="#6d28d9" strokeWidth="1.5"/>
            {/* Hand circles */}
            <circle cx="25" cy="196" r="10" fill="#0d0618" stroke="#7c3aed" strokeWidth="1.5"/>
            <circle cx="175" cy="196" r="10" fill="#0d0618" stroke="#7c3aed" strokeWidth="1.5"/>
            {/* Legs */}
            <rect x="62" y="204" width="32" height="60" rx="12" fill="#110726" stroke="#6d28d9" strokeWidth="1.5"/>
            <rect x="106" y="204" width="32" height="60" rx="12" fill="#110726" stroke="#6d28d9" strokeWidth="1.5"/>
            {/* Feet */}
            <rect x="55" y="252" width="42" height="16" rx="8" fill="#0d0618" stroke="#7c3aed" strokeWidth="1.5"/>
            <rect x="103" y="252" width="42" height="16" rx="8" fill="#0d0618" stroke="#7c3aed" strokeWidth="1.5"/>
          </svg>
        </Box>

        <SectionTag>Powered by Groq AI · Llama 3.3</SectionTag>
        <SectionTitle>NOVA AI</SectionTitle>
        <SectionDesc>
          Your intelligent space companion. Ask anything about solar storms,
          exoplanet atmospheres, space missions, or the cosmos — NOVA knows it all.
        </SectionDesc>
        <GhostButton onClick={() => navigate('/ai-chat')}>Chat with NOVA</GhostButton>
      </FullSection>

      {/* ══════════════════════════════════════════════════════════════
          LIVE ISS TRACKER — green glow
      ══════════════════════════════════════════════════════════════ */}
      <FullSection
        id="iss-tracker"
        imgUrl={IMAGES.iss}
        overlay="linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,12,5,0.6) 50%, rgba(0,0,0,0.25) 100%)"
      >
        {/* green glow orb */}
        <Box sx={{
          position: 'absolute', top: '18%', right: { xs: '-5%', md: '6%' },
          width: { xs: 170, md: 420 }, height: { xs: 170, md: 420 },
          borderRadius: '50%', pointerEvents: 'none', zIndex: 0,
          background: 'radial-gradient(circle, rgba(20,200,100,0.18) 0%, rgba(0,150,60,0.08) 45%, transparent 70%)',
          filter: 'blur(28px)',
        }} />
        <SectionTag>Real-time · Open Notify API</SectionTag>
        <SectionTitle>Live ISS Tracker</SectionTitle>
        <SectionDesc>
          Track the International Space Station in real-time on an interactive
          world map. See current crew, altitude, speed and pass times for your city.
        </SectionDesc>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <GhostButton onClick={() => navigate('/iss-tracker')}>Track Now</GhostButton>
          <GhostButton onClick={() => navigate('/space-missions')}>Space Missions</GhostButton>
          <GhostButton onClick={() => navigate('/alerts#iss-pass')}>ISS Pass Alerts</GhostButton>
        </Box>
      </FullSection>

      {/* ══════════════════════════════════════════════════════════════
          SATELLITE TRACKER — cyan glow
      ══════════════════════════════════════════════════════════════ */}
      <FullSection
        id="satellite-tracker"
        imgUrl={IMAGES.satellite}
        overlay="linear-gradient(to top, rgba(0,0,0,0.93) 0%, rgba(0,8,18,0.62) 50%, rgba(0,0,0,0.25) 100%)"
      >
        {/* cyan glow orb */}
        <Box sx={{
          position: 'absolute', top: '22%', left: { xs: '-5%', md: '8%' },
          width: { xs: 160, md: 400 }, height: { xs: 160, md: 400 },
          borderRadius: '50%', pointerEvents: 'none', zIndex: 0,
          background: 'radial-gradient(circle, rgba(0,200,240,0.18) 0%, rgba(0,150,200,0.08) 45%, transparent 70%)',
          filter: 'blur(28px)',
        }} />
        <SectionTag>Live TLE Data · Satellite Orbits</SectionTag>
        <SectionTitle>Satellite Tracker</SectionTitle>
        <SectionDesc>
          Visualize thousands of satellites orbiting Earth in real-time.
          Track orbital paths, predict passes and explore the full constellation
          of humanity's infrastructure in space.
        </SectionDesc>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <GhostButton onClick={() => navigate('/satellite-tracker')}>Open Tracker</GhostButton>
          <GhostButton onClick={() => navigate('/night-sky')}>Night Sky</GhostButton>
        </Box>
      </FullSection>

      {/* ══════════════════════════════════════════════════════════════
          MOON DASHBOARD — silver/gold lunar glow
      ══════════════════════════════════════════════════════════════ */}
      <FullSection
        id="moon-dashboard"
        imgUrl={IMAGES.moon}
        overlay="linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(4,6,18,0.55) 50%, rgba(0,0,0,0.18) 100%)"
      >
        {/* silver-gold glow orb */}
        <Box sx={{
          position: 'absolute', top: '15%', right: { xs: '-8%', md: '4%' },
          width: { xs: 200, md: 500 }, height: { xs: 200, md: 500 },
          borderRadius: '50%', pointerEvents: 'none', zIndex: 0,
          background: 'radial-gradient(circle, rgba(255,236,153,0.2) 0%, rgba(200,190,120,0.08) 45%, transparent 70%)',
          filter: 'blur(32px)',
        }} />
        <SectionTag>Real-time Ephemeris · NASA APOD</SectionTag>
        <SectionTitle>Moon Dashboard</SectionTitle>
        <SectionDesc>
          Track the Moon's live phase, illumination, rise & set times, and exact position
          in the sky. Explore craters, maria, and mountain ranges — powered by real
          astronomical algorithms and NASA's daily space photo.
        </SectionDesc>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <GhostButton onClick={() => navigate('/moon')}>Open Moon Dashboard</GhostButton>
          <GhostButton onClick={() => navigate('/night-sky')}>Night Sky Map</GhostButton>
        </Box>
      </FullSection>

      {/* ══════════════════════════════════════════════════════════════
          LIGHT POLLUTION MAP — NASA Black Marble night earth
      ══════════════════════════════════════════════════════════════ */}
      <FullSection
        id="light-pollution"
        imgUrl={IMAGES.nightEarth}
        overlay="linear-gradient(to top, rgba(0,0,0,0.93) 0%, rgba(0,4,12,0.55) 50%, rgba(0,0,0,0.18) 100%)"
      >
        {/* teal-blue glow orb matching city-light color palette */}
        <Box sx={{
          position: 'absolute', top: '15%', left: { xs: '-8%', md: '6%' },
          width: { xs: 180, md: 460 }, height: { xs: 180, md: 460 },
          borderRadius: '50%', pointerEvents: 'none', zIndex: 0,
          background: 'radial-gradient(circle, rgba(0,180,255,0.18) 0%, rgba(0,100,200,0.08) 45%, transparent 70%)',
          filter: 'blur(30px)',
        }} />

        {/* small stat chips */}
        <Box sx={{
          display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: { xs: 1.5, md: 2.5 },
        }}>
          {[
            { label: 'VIIRS 2024', color: 'rgba(76,175,80,0.85)' },
            { label: 'Bortle 1–9', color: 'rgba(255,255,255,0.35)' },
            { label: 'Live Sky Quality', color: 'rgba(255,255,255,0.35)' },
            { label: 'Dark Spot Finder', color: 'rgba(255,255,255,0.35)' },
          ].map(({ label, color }) => (
            <Box key={label} sx={{
              px: 1.5, py: 0.4,
              border: `1px solid ${color}`,
              borderRadius: 0,
              fontSize: { xs: 9, md: 10 },
              fontWeight: 600, letterSpacing: 1.5,
              textTransform: 'uppercase',
              color: color === 'rgba(76,175,80,0.85)' ? '#4caf50' : 'rgba(255,255,255,0.55)',
              fontFamily: 'Inter, sans-serif',
            }}>
              {label}
            </Box>
          ))}
        </Box>

        <SectionTag>NASA Black Marble · VIIRS Satellite · Colorado School of Mines</SectionTag>
        <SectionTitle>Light Pollution Map</SectionTitle>
        <SectionDesc>
          Explore Earth's night sky through real VIIRS satellite imagery. Measure sky
          quality with the Bortle scale, find the darkest spots within 50 km, and
          track how light pollution has changed from 2016 to today.
        </SectionDesc>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <GhostButton light onClick={() => navigate('/stargazing')}>Open Map</GhostButton>
          <GhostButton onClick={() => navigate('/stargazing')}>Find Dark Spots</GhostButton>
        </Box>
      </FullSection>

      {/* ══════════════════════════════════════════════════════════════
          QUICK LINKS GRID — 12 tiles (SpaceX product grid style)
      ══════════════════════════════════════════════════════════════ */}
      <Box sx={{ position: 'relative', zIndex: 1, bgcolor: '#000', scrollSnapAlign: 'start' }}>
        <Box sx={{
          borderTop: '1px solid rgba(255,255,255,0.08)',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(3, 1fr)',
            sm: 'repeat(4, 1fr)',
            md: 'repeat(6, 1fr)',
            lg: 'repeat(12, 1fr)',
          },
        }}>
          {quickLinks.map((item) => (
            <FeatureTile
              key={item.path}
              icon={item.icon}
              label={item.label}
              path={item.path}
              onClick={navigate}
            />
          ))}
        </Box>
      </Box>

      {/* ══════════════════════════════════════════════════════════════
          STATS SECTION
      ══════════════════════════════════════════════════════════════ */}
      <Box sx={{
        bgcolor: '#000',
        py: { xs: 5, md: 12 },
        px: { xs: 3, md: 10 },
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: { xs: 3, md: 12 },
      }}>
        {[
          { value: '5,000+', label: 'Events Tracked'      },
          { value: '5,000+', label: 'Exoplanets'          },
          { value: '32+',    label: 'Space Missions'      },
          { value: '14+',    label: 'Live Features'       },
          { value: '8',      label: 'NASA APIs'           },
        ].map(({ value, label }) => (
          <Box key={label} sx={{ textAlign: 'center', minWidth: { xs: 100, md: 120 } }}>
            <Typography sx={{
              fontSize: { xs: '2.2rem', md: '3.2rem' },
              fontWeight: 800, color: '#fff',
              fontFamily: 'Inter, sans-serif', lineHeight: 1,
            }}>
              {value}
            </Typography>
            <Box sx={{ width: 28, height: 2, bgcolor: 'rgba(255,255,255,0.25)', mx: 'auto', my: 1 }} />
            <Typography sx={{
              fontSize: { xs: 10, md: 12 },
              color: 'rgba(255,255,255,0.4)',
              letterSpacing: 1.5,
              textTransform: 'uppercase',
              fontFamily: 'Inter, sans-serif',
            }}>
              {label}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* ══════════════════════════════════════════════════════════════
          FOOTER
      ══════════════════════════════════════════════════════════════ */}
      <Box sx={{
        bgcolor: '#000',
        borderTop: '1px solid rgba(255,255,255,0.08)',
        py: { xs: 4, md: 5 },
        px: { xs: 3, sm: 5, md: 10 },
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'flex-start', md: 'center' },
        justifyContent: 'space-between',
        gap: 4,
      }}>
        {/* Logo */}
        <SpacePulseLogo size={38} showText />

        {/* Credit */}
        <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
          <Typography sx={{
            fontSize: 11, color: 'rgba(255,255,255,0.25)',
            fontFamily: 'Inter, sans-serif', letterSpacing: 0.5,
          }}>
            Powered by NASA Open APIs
          </Typography>
          <Typography sx={{
            fontSize: 12, color: 'rgba(255,255,255,0.25)',
            fontFamily: 'Inter, sans-serif', mt: 0.3,
          }}>
            Developed by{' '}
            <Box component="span" sx={{ color: 'rgba(255,255,255,0.65)', fontWeight: 600 }}>
              Yash Lase
            </Box>
          </Typography>
        </Box>
      </Box>

    </Box>
  );
}
